import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { emailService } from '../../services/emailService';
import { projectService } from '../../services/projectService';
import { Send } from 'lucide-react';
import { useCompanyInfo } from '../../hooks/useCompanyInfo';

// Helper to format currency
const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD'
    }).format(amount || 0);
};

// Helper to format dates
const formatDate = (dateString) => {
    if (!dateString) return new Date().toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
    });
    return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
    });
};

/**
 * Compute total contract amount and total paid (including any change orders).
 */
const computeProjectTotals = (project) => {
    const baseContract = parseFloat(project?.contract_price || 0);
    const changeOrders = (project?.change_orders || [])
        .map(co => ({
            date: co?.date || co?.added_at || null,
            scope: co?.scope_of_work || co?.description || 'Change Order',
            amount: parseFloat(co?.amount) || 0,
        }))
        .sort((a, b) => {
            if (!a.date && !b.date) return 0;
            if (!a.date) return 1;
            if (!b.date) return -1;
            return new Date(a.date) - new Date(b.date);
        });
    const changeOrdersTotal = changeOrders.reduce((sum, co) => sum + co.amount, 0);
    const totalContract = baseContract + changeOrdersTotal;

    const paidPayments = (project?.payment_schedule || [])
        .filter(p => p?.status === 'paid' || p?.paid_date)
        .map(p => ({
            description: p?.description || `Payment ${p?.payment_number || ''}`.trim() || 'Payment',
            amount: parseFloat(p?.amount) || 0,
            paid_date: p?.paid_date || null,
        }))
        .sort((a, b) => {
            // Earliest paid first; nulls last
            if (!a.paid_date && !b.paid_date) return 0;
            if (!a.paid_date) return 1;
            if (!b.paid_date) return -1;
            return new Date(a.paid_date) - new Date(b.paid_date);
        });
    const totalPaid = paidPayments.reduce((sum, p) => sum + p.amount, 0);

    const remaining = Math.max(0, totalContract - totalPaid);
    return { totalContract, totalPaid, remaining, paidPayments, baseContract, changeOrders, changeOrdersTotal };
};

/**
 * Generates the HTML for the payment receipt email.
 * This structure is based on your InvoiceDetail.jsx
 */
const generateReceiptHtml = (payment, customer, project, companyInfo) => {
    const { totalContract, totalPaid, remaining, paidPayments, baseContract, changeOrders, changeOrdersTotal } = computeProjectTotals(project);
    const isPaidInFull = remaining <= 0.01; // tolerate floating point

    // Change orders block (only if there are any)
    const changeOrderRows = (changeOrders || []).map(co => {
        const sign = co.amount >= 0 ? '+' : '−';
        const color = co.amount >= 0 ? '#b45309' : '#059669';
        return `
        <tr>
          <td style="padding: 6px 0; color: #555; font-size: 13px;">${co.scope}</td>
          <td style="padding: 6px 8px; color: #555; font-size: 13px; text-align: center;">${co.date ? formatDate(co.date) : '—'}</td>
          <td style="padding: 6px 0; color: ${color}; font-size: 13px; text-align: right; font-weight: 500;">${sign}${formatCurrency(Math.abs(co.amount))}</td>
        </tr>
        `;
    }).join('');

    const changeOrdersBlock = (changeOrders && changeOrders.length > 0) ? `
          <div style="margin-top: 24px;">
            <div style="font-weight: 600; color: #333; margin-bottom: 8px; font-size: 14px;">CONTRACT TOTAL BREAKDOWN</div>
            <table style="width: 100%; border-collapse: collapse; border-top: 1px solid #ddd;">
              <thead>
                <tr style="border-bottom: 1px solid #ddd;">
                  <th style="text-align: left; padding: 6px 0; font-size: 12px; color: #777; text-transform: uppercase;">Item</th>
                  <th style="text-align: center; padding: 6px 8px; font-size: 12px; color: #777; text-transform: uppercase;">Date</th>
                  <th style="text-align: right; padding: 6px 0; font-size: 12px; color: #777; text-transform: uppercase;">Amount</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td style="padding: 6px 0; color: #333; font-size: 13px; font-weight: 500;">Original Contract</td>
                  <td style="padding: 6px 8px; color: #555; font-size: 13px; text-align: center;">—</td>
                  <td style="padding: 6px 0; color: #333; font-size: 13px; text-align: right; font-weight: 500;">${formatCurrency(baseContract)}</td>
                </tr>
                ${changeOrderRows}
                <tr style="border-top: 2px solid #ccc; font-weight: bold;">
                  <td style="padding: 8px 0; color: #333;">Total Contract</td>
                  <td></td>
                  <td style="padding: 8px 0; text-align: right; color: #333;">${formatCurrency(totalContract)}</td>
                </tr>
              </tbody>
            </table>
          </div>
    ` : '';

    // Per-payment history table
    const paymentRows = (paidPayments || []).map(p => `
        <tr>
          <td style="padding: 6px 0; color: #555; font-size: 13px;">${p.description}</td>
          <td style="padding: 6px 8px; color: #555; font-size: 13px; text-align: center;">${p.paid_date ? formatDate(p.paid_date) : '—'}</td>
          <td style="padding: 6px 0; color: #333; font-size: 13px; text-align: right; font-weight: 500;">${formatCurrency(p.amount)}</td>
        </tr>
    `).join('');

    const paymentsTable = paidPayments && paidPayments.length > 0 ? `
          <div style="margin-top: 24px;">
            <div style="font-weight: 600; color: #333; margin-bottom: 8px; font-size: 14px;">PAYMENT HISTORY</div>
            <table style="width: 100%; border-collapse: collapse; border-top: 1px solid #ddd;">
              <thead>
                <tr style="border-bottom: 1px solid #ddd;">
                  <th style="text-align: left; padding: 6px 0; font-size: 12px; color: #777; text-transform: uppercase;">Payment</th>
                  <th style="text-align: center; padding: 6px 8px; font-size: 12px; color: #777; text-transform: uppercase;">Date Paid</th>
                  <th style="text-align: right; padding: 6px 0; font-size: 12px; color: #777; text-transform: uppercase;">Amount</th>
                </tr>
              </thead>
              <tbody>
                ${paymentRows}
                <tr style="border-top: 2px solid #ccc; font-weight: bold;">
                  <td style="padding: 8px 0; color: #333;">Total Paid to Date</td>
                  <td></td>
                  <td style="padding: 8px 0; text-align: right; color: #059669;">${formatCurrency(totalPaid)}</td>
                </tr>
              </tbody>
            </table>
          </div>
    ` : '';

    const balanceBlock = isPaidInFull ? `
          ${changeOrdersBlock}
          ${paymentsTable}
          <div style="margin-top: 24px; padding: 16px; background-color: #d1fae5; border: 2px solid #6ee7b7; border-radius: 8px; text-align: center;">
            <div style="font-size: 22px; font-weight: bold; color: #065f46; letter-spacing: 1px;">
              ✓ PAID IN FULL
            </div>
            <div style="font-size: 13px; color: #065f46; margin-top: 4px;">
              Thank you — your project balance is $0.
            </div>
          </div>
    ` : `
          ${changeOrdersBlock}
          ${paymentsTable}
          <div style="margin-top: 20px; max-width: 50%; margin-left: auto; text-align: right;">
            <div style="display: flex; justify-content: space-between; font-size: 14px; color: #555; padding-top: 6px;">
              <span>Total Contract:</span>
              <span>${formatCurrency(totalContract)}</span>
            </div>
            <div style="display: flex; justify-content: space-between; font-size: 18px; font-weight: bold; color: #b45309; padding-top: 8px; border-top: 1px solid #ccc; margin-top: 8px;">
              <span>Remaining Balance:</span>
              <span>${formatCurrency(remaining)}</span>
            </div>
          </div>
    `;

    return `
      <div style="font-family: Arial, sans-serif; max-width: 800px; margin: auto; border: 1px solid #ddd; border-radius: 8px; overflow: hidden;">
        <div style="background-color: #4478bb; color: white; padding: 32px;">
          <div style="display: flex; justify-content: space-between; align-items: start;">
            <div>
              <h2 style="font-size: 28px; font-weight: bold; margin: 0;">${companyInfo.name}</h2>
              <p style="margin-top: 8px;">${companyInfo.address1}</p>
              <p>${companyInfo.address2}</p>
              <p style="margin-top: 8px;">${companyInfo.phone}</p>
              <p>${companyInfo.email}</p>
              <p style="margin-top: 8px;">CSLB License: ${companyInfo.license}</p>
            </div>
            <div style="text-align: right;">
              <h3 style="font-size: 24px; font-weight: bold; margin: 0;">PAYMENT RECEIPT</h3>
              <div style="margin-top: 16px; background-color: #d1fae5; color: #065f46; border: 1px solid #6ee7b7; padding: 4px 12px; border-radius: 9999px; display: inline-block; font-weight: 600;">
                ✓ PAID
              </div>
            </div>
          </div>
        </div>
        
        <div style="padding: 32px; display: grid; grid-template-columns: 1fr 1fr; gap: 32px; border-bottom: 1px solid #ddd;">
          <div>
            <h3 style="font-weight: 600; color: #333; margin-bottom: 8px;">BILLED TO:</h3>
            <p style="font-weight: 500;">${customer.name}</p>
            <p style="color: #555;">${customer.address || ''}</p>
            <p style="color: #555;">${customer.phone || ''}</p>
            <p style="color: #555;">${customer.email}</p>
          </div>
          <div style="text-align: right;">
            <div style="margin-bottom: 16px;">
              <p style="color: #555;">Date Paid</p>
              <p style="font-weight: 500;">${formatDate(payment.paid_date)}</p>
            </div>
            <div>
              <p style="color: #555;">Project</p>
              <p style="font-weight: 500;">${project.name}</p>
            </div>
          </div>
        </div>
        
        <div style="padding: 32px;">
          <table style="width: 100%; border-collapse: collapse;">
            <thead>
              <tr style="border-bottom: 2px solid #ccc;">
                <th style="text-align: left; padding-bottom: 12px; font-weight: 600; color: #555;">DESCRIPTION</th>
                <th style="text-align: right; padding-bottom: 12px; font-weight: 600; width: 100px; color: #555;">TOTAL</th>
              </tr>
            </thead>
            <tbody>
              <tr style="border-bottom: 1px solid #eee;">
                <td style="padding: 16px 0;">${payment.description}</td>
                <td style="padding: 16px 0; text-align: right; font-weight: 500; color: #333;">${formatCurrency(payment.amount)}</td>
              </tr>
            </tbody>
          </table>
        </div>
        
        <div style="padding: 32px; background-color: #f9f9f9; border-top: 1px solid #ddd;">
          <div style="max-width: 45%; margin-left: auto; text-align: right;">
            <div style="display: flex; justify-content: space-between; font-size: 20px; font-weight: bold; color: #333; padding-top: 8px; border-top: 2px solid #ccc;">
              <span>AMOUNT PAID:</span>
              <span style="color: #059669;">${formatCurrency(payment.amount)}</span>
            </div>
          </div>
          ${balanceBlock}
        </div>

        <div style="padding: 32px; text-align: center; color: #777; font-size: 14px; border-top: 1px solid #ddd;">
            <p>Thank you for your business!</p>
        </div>
      </div>
    `;
};


export const SendPaymentReceiptDialog = ({ open, onOpenChange, payment, project, customer, onReceiptSent }) => {
    const { toast } = useToast();
    const companyInfo = useCompanyInfo();
    const [isSending, setIsSending] = useState(false);
    
    // Form state - pre-filled and editable as requested
    const [toEmail, setToEmail] = useState(customer?.email || "");
    const [subject, setSubject] = useState(`Payment Receipt for ${project?.name}`);
    const [description, setDescription] = useState(payment?.description || "Payment");
    const [amount, setAmount] = useState(payment?.amount || 0);
    const [paidDate, setPaidDate] = useState(payment?.paid_date ? payment.paid_date.split('T')[0] : new Date().toISOString().split('T')[0]);

    // Update state if props change (e.g., user clicks another payment receipt)
    useEffect(() => {
        if (payment && project && customer) {
            setToEmail(customer.email || "");
            setSubject(`Payment Receipt for ${project.name}`);
            setDescription(payment.description || "Payment");
            setAmount(payment.amount || 0);
            setPaidDate(payment.paid_date ? payment.paid_date.split('T')[0] : new Date().toISOString().split('T')[0]);
        }
    }, [payment, project, customer]);

    const handleSendReceipt = async () => {
        setIsSending(true);

        // 1. Construct the payment object from editable state
        const receiptData = {
            description: description,
            amount: parseFloat(amount),
            paid_date: paidDate
        };

        // 2. Generate the HTML body
        const htmlBody = generateReceiptHtml(receiptData, customer, project, companyInfo);

        // 3. Send using the generic emailService
        try {
            await emailService.send({
                to_email: toEmail,
                to_name: customer.name,
                subject: subject,
                body: htmlBody,
                customer_id: customer.id || customer._id,
                project_id: project.id || project._id,
                is_html: true // Set this to true
            });

            // Stamp the payment record with the send timestamp so the icon turns green
            try {
                if (payment?.payment_number) {
                    await projectService.updatePayment(
                        project.id || project._id,
                        payment.payment_number,
                        { receipt_sent_at: new Date().toISOString() }
                    );
                }
            } catch (stampErr) {
                console.warn("Receipt sent but failed to stamp payment record:", stampErr);
            }

            toast({
                title: "Success",
                description: "Payment receipt sent successfully."
            });

            // Call the refresh function passed from parent
            if (onReceiptSent) {
                onReceiptSent();
            }

            onOpenChange(false); // Close dialog on success
        } catch (error) {
            console.error('Error sending receipt:', error);
            toast({
                title: "Error",
                description: error.detail || "Failed to send receipt. Please try again.",
                variant: "destructive"
            });
        } finally {
            setIsSending(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-lg">
                <DialogHeader>
                    <DialogTitle>Send Payment Receipt</DialogTitle>
                    <DialogDescription>
                        Review and edit the details below before sending the receipt to {customer?.name}.
                    </DialogDescription>
                </DialogHeader>
                
                <div className="space-y-4 py-4">
                    <div className="grid grid-cols-4 items-center gap-4">
                        <Label htmlFor="to_email" className="text-right">
                            To Email
                        </Label>
                        <Input
                            id="to_email"
                            value={toEmail}
                            onChange={(e) => setToEmail(e.target.value)}
                            className="col-span-3"
                        />
                    </div>
                    <div className="grid grid-cols-4 items-center gap-4">
                        <Label htmlFor="subject" className="text-right">
                            Subject
                        </Label>
                        <Input
                            id="subject"
                            value={subject}
                            onChange={(e) => setSubject(e.target.value)}
                            className="col-span-3"
                        />
                    </div>
                    <div className="grid grid-cols-4 items-center gap-4">
                        <Label htmlFor="description" className="text-right">
                            Description
                        </Label>
                        <Textarea
                            id="description"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            className="col-span-3"
                            rows={3}
                        />
                    </div>
                    <div className="grid grid-cols-4 items-center gap-4">
                        <Label htmlFor="amount" className="text-right">
                            Amount
                        </Label>
                        <Input
                            id="amount"
                            type="number"
                            value={amount}
                            onChange={(e) => setAmount(e.target.value)}
                            className="col-span-3"
                        />
                    </div>
                    <div className="grid grid-cols-4 items-center gap-4">
                        <Label htmlFor="paid_date" className="text-right">
                            Paid Date
                        </Label>
                        <Input
                            id="paid_date"
                            type="date"
                            value={paidDate}
                            onChange={(e) => setPaidDate(e.target.value)}
                            className="col-span-3"
                        />
                    </div>
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSending}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSendReceipt}
                        disabled={isSending}
                        className="bg-brand-600 hover:bg-brand-700"
                    >
                        {isSending ? "Sending..." : <><Send className="mr-2 h-4 w-4" /> Send Receipt</>}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
